import ExcelJS from "exceljs";
import { Invoice } from "../types";
import { Service } from "../types/service";

const HEADER_FILL = "FF1F4E78";
const SECTION_FILL = "FFD9EAF7";
const LABEL_FILL = "FFEAF2F8";
const BORDER_COLOR = "FF7F8C8D";
const PHOTO_WIDTH_PX = 108;
const PHOTO_HEIGHT_PX = 80;

type SupportedImageExtension = "jpeg" | "png" | "gif";

const thinBorder: Partial<ExcelJS.Borders> = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

const asText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const asNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getImageExtension = (
  contentType: string | null,
  url: string,
  buffer: ArrayBuffer,
): SupportedImageExtension | null => {
  const bytes = new Uint8Array(buffer);
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "png";
  }
  if (
    bytes.length >= 3 &&
    bytes[0] === 0xff &&
    bytes[1] === 0xd8 &&
    bytes[2] === 0xff
  ) {
    return "jpeg";
  }
  if (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38 &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61
  ) {
    return "gif";
  }

  const normalizedContentType = (contentType || "").toLowerCase();
  if (normalizedContentType.includes("image/png")) return "png";
  if (normalizedContentType.includes("image/gif")) return "gif";
  if (
    normalizedContentType.includes("image/jpeg") ||
    normalizedContentType.includes("image/jpg")
  ) {
    return "jpeg";
  }

  const extensionMatch = url.match(/\.(png|gif|jpe?g)(?:[?&#]|$)/i);
  if (!extensionMatch) return null;
  return extensionMatch[1].toLowerCase() === "jpg"
    ? "jpeg"
    : (extensionMatch[1].toLowerCase() as SupportedImageExtension);
};

const estimateStructureRowHeight = (observation: unknown): number => {
  const text = asText(observation);
  const estimatedLines = text
    .split(/\r?\n/)
    .reduce((lines, segment) => lines + Math.max(1, Math.ceil(segment.length / 38)), 0);
  return Math.max(66, estimatedLines * 15);
};

const embedStructurePhoto = async (
  workbook: ExcelJS.Workbook,
  worksheet: ExcelJS.Worksheet,
  rowNumber: number,
  photoUrl: string,
): Promise<boolean> => {
  const normalizedPhotoUrl = photoUrl.trim();
  if (!/^https?:\/\//i.test(normalizedPhotoUrl)) return false;

  try {
    const response = await fetch(normalizedPhotoUrl);
    if (!response.ok) {
      console.warn(`Could not embed structure photo in Excel: HTTP ${response.status}`);
      return false;
    }

    const imageBuffer = await response.arrayBuffer();
    const extension = getImageExtension(
      response.headers.get("content-type"),
      normalizedPhotoUrl,
      imageBuffer,
    );
    if (!extension) {
      console.warn("Could not embed structure photo in Excel: unsupported image format");
      return false;
    }

    const imageId = workbook.addImage({ buffer: imageBuffer, extension });
    worksheet.getCell(rowNumber, 3).value = null;
    worksheet.addImage(imageId, {
      tl: { col: 2.08, row: rowNumber - 0.92 },
      ext: { width: PHOTO_WIDTH_PX, height: PHOTO_HEIGHT_PX },
      editAs: "oneCell",
      hyperlinks: {
        hyperlink: normalizedPhotoUrl,
        tooltip: "Abrir foto original",
      },
    });
    return true;
  } catch (error) {
    console.warn("Could not embed structure photo in Excel:", error);
    return false;
  }
};

const resolveFactor = (service: Service, currentFactor: number): number => {
  const serviceFactor = Number(service.factor);
  return Number.isFinite(serviceFactor) && serviceFactor > 0
    ? serviceFactor
    : currentFactor;
};

const normalizeServices = (
  services: Invoice["services"] | Record<string, Service> | null | undefined,
): Service[] => {
  if (!services) return [];
  if (Array.isArray(services)) return services;

  return Object.keys(services)
    .sort((a, b) => {
      const first = Number(a);
      const second = Number(b);
      return Number.isFinite(first) && Number.isFinite(second)
        ? first - second
        : a.localeCompare(b);
    })
    .map((key) => services[key]);
};

const addSectionTitle = (
  worksheet: ExcelJS.Worksheet,
  rowNumber: number,
  title: string,
  lastColumn: string,
) => {
  worksheet.mergeCells(`A${rowNumber}:${lastColumn}${rowNumber}`);
  const cell = worksheet.getCell(`A${rowNumber}`);
  cell.value = title;
  cell.font = { bold: true, color: { argb: "FF1F1F1F" } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SECTION_FILL } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = thinBorder;
  worksheet.getRow(rowNumber).height = 22;
};

const styleHeaderRow = (row: ExcelJS.Row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = thinBorder;
  });
  row.height = 26;
};

const styleTableRows = (
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  endRow: number,
  columnCount: number,
) => {
  for (let rowNumber = startRow; rowNumber <= endRow; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    for (let column = 1; column <= columnCount; column += 1) {
      const cell = row.getCell(column);
      cell.border = thinBorder;
      cell.alignment = { vertical: "top", wrapText: true };
    }
  }
};

const addDetailRow = (
  worksheet: ExcelJS.Worksheet,
  rowNumber: number,
  firstLabel: string,
  firstValue: unknown,
  secondLabel = "",
  secondValue: unknown = "",
) => {
  worksheet.mergeCells(`B${rowNumber}:C${rowNumber}`);
  worksheet.mergeCells(`E${rowNumber}:G${rowNumber}`);
  const row = worksheet.getRow(rowNumber);
  row.getCell(1).value = firstLabel;
  row.getCell(2).value = firstValue instanceof Date ? firstValue : asText(firstValue);
  row.getCell(4).value = secondLabel;
  row.getCell(5).value = secondValue instanceof Date ? secondValue : asText(secondValue);

  [1, 4].forEach((column) => {
    const cell = row.getCell(column);
    cell.font = { bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: LABEL_FILL } };
  });

  for (let column = 1; column <= 7; column += 1) {
    const cell = row.getCell(column);
    cell.border = thinBorder;
    cell.alignment = { vertical: "top", wrapText: true };
  }
};

export const buildInvoiceWorkbook = async (
  invoice: Invoice,
  currentFactor: number,
  generatedAt = new Date(),
): Promise<ExcelJS.Workbook> => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Orca";
  workbook.created = generatedAt;

  const worksheet = workbook.addWorksheet("Relatório", {
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      paperSize: 9,
      margins: {
        left: 0.25,
        right: 0.25,
        top: 0.5,
        bottom: 0.5,
        header: 0.2,
        footer: 0.2,
      },
    },
  });

  worksheet.columns = [
    { key: "a", width: 18 },
    { key: "b", width: 42 },
    { key: "c", width: 16 },
    { key: "d", width: 22 },
    { key: "e", width: 12 },
    { key: "f", width: 12 },
    { key: "g", width: 18 },
  ];

  worksheet.mergeCells("A1:G1");
  const companyCell = worksheet.getCell("A1");
  companyCell.value = "ORCA INSTALAÇÕES ELÉTRICAS E CIVIL";
  companyCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  companyCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  companyCell.alignment = { horizontal: "center", vertical: "middle" };
  companyCell.border = thinBorder;
  worksheet.getRow(1).height = 30;

  worksheet.mergeCells("A2:G2");
  const titleCell = worksheet.getCell("A2");
  titleCell.value = "PLANILHA DE FATURAMENTO DE SERVIÇO DE LINHA VIVA";
  titleCell.font = { bold: true, size: 13 };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  titleCell.border = thinBorder;
  worksheet.getRow(2).height = 24;

  const header = invoice.header || ({} as Invoice["header"]);
  addSectionTitle(worksheet, 4, "DADOS DO BOLETIM", "G");
  addDetailRow(worksheet, 5, "Contrato", "4600035150", "Planilha", invoice.id);
  addDetailRow(worksheet, 6, "Título", header.title || invoice.title, "Projeto", header.project || invoice.project);
  addDetailRow(worksheet, 7, "Município", header.city || invoice.city, "Data", header.date || invoice.date);
  addDetailRow(worksheet, 8, "S.E.", header.se, "OS", header.os || invoice.os);
  addDetailRow(worksheet, 9, "Setor", header.sector, "PES", header.si);
  addDetailRow(worksheet, 10, "PMS", header.pms || invoice.pms, "ALI", header.ali);
  addDetailRow(worksheet, 11, "KM inicial", header.kmInit, "KM final", header.kmEnd);
  addDetailRow(worksheet, 12, "Equipe", header.team || invoice.team, "Resp. Coelba", header.respCoelba);
  addDetailRow(worksheet, 13, "Resp. Orca", header.respOrca, "Oper./H. rec.", header.operHRec);
  addDetailRow(worksheet, 14, "Oper./H. devol.", header.operHDev, "Impresso em", generatedAt);
  worksheet.getCell("E14").numFmt = "dd/mm/yyyy hh:mm";

  addSectionTitle(worksheet, 16, "SERVIÇOS", "G");
  const servicesHeaderRow = worksheet.getRow(17);
  servicesHeaderRow.values = [
    "CÓDIGO",
    "SERVIÇO",
    "QTD.",
    "VL. UNIT.",
    "UN.",
    "FATOR",
    "TOTAL PARCIAL",
  ];
  styleHeaderRow(servicesHeaderRow);

  const services = normalizeServices(invoice.services as Invoice["services"] | Record<string, Service>);
  let currentRow = 18;
  let calculatedTotal = 0;

  if (services.length === 0) {
    worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = "Nenhum serviço registrado para este boletim.";
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "center" };
    styleTableRows(worksheet, currentRow, currentRow, 7);
    currentRow += 1;
  } else {
    services.forEach((service) => {
      const quantity = asNumber(service.quantity);
      const price = asNumber(service.price);
      const factor = resolveFactor(service, currentFactor);
      const partialTotal = quantity * price * factor;
      calculatedTotal += partialTotal;

      const paidService = service as Service & { paid?: boolean; pedido?: string };
      const displayCode =
        paidService.paid && paidService.pedido
          ? `${asText(service.code)} (${paidService.pedido})`
          : asText(service.code);

      const row = worksheet.getRow(currentRow);
      row.values = [
        displayCode,
        service.activity || service.description || "",
        quantity,
        price,
        service.unid || "UN",
        factor,
        { formula: `C${currentRow}*D${currentRow}*F${currentRow}`, result: partialTotal },
      ];
      row.getCell(3).numFmt = "#,##0.00";
      row.getCell(4).numFmt = "#,##0.00";
      row.getCell(6).numFmt = "0.00";
      row.getCell(7).numFmt = "#,##0.00";
      currentRow += 1;
    });
    styleTableRows(worksheet, 18, currentRow - 1, 7);
  }

  const totalRow = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
  totalRow.getCell(1).value = "TOTAL";
  totalRow.getCell(1).font = { bold: true };
  totalRow.getCell(1).alignment = { horizontal: "right" };
  totalRow.getCell(7).value = services.length
    ? { formula: `SUM(G18:G${currentRow - 1})`, result: calculatedTotal }
    : 0;
  totalRow.getCell(7).font = { bold: true };
  totalRow.getCell(7).numFmt = "#,##0.00";
  for (let column = 1; column <= 7; column += 1) {
    const cell = totalRow.getCell(column);
    cell.border = thinBorder;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: LABEL_FILL } };
  }
  currentRow += 2;

  addSectionTitle(worksheet, currentRow, "OBSERVAÇÃO", "G");
  currentRow += 1;
  worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
  const observationCell = worksheet.getCell(`A${currentRow}`);
  observationCell.value = header.observation || "Nenhuma observação informada.";
  observationCell.alignment = { vertical: "top", wrapText: true };
  observationCell.border = thinBorder;
  worksheet.getRow(currentRow).height = 45;
  currentRow += 2;

  addSectionTitle(worksheet, currentRow, "ESTRUTURAS", "G");
  currentRow += 1;
  const structuresHeaderRow = worksheet.getRow(currentRow);
  structuresHeaderRow.values = ["PG", "OBSERVAÇÕES DO PG", "FOTO"];
  styleHeaderRow(structuresHeaderRow);
  const structureStartRow = currentRow + 1;
  currentRow += 1;

  const photos = Array.isArray(invoice.photos)
    ? invoice.photos.filter((photo) => photo && typeof photo === "object")
    : [];

  if (photos.length === 0) {
    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = "Nenhuma estrutura registrada para este boletim.";
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "center" };
    currentRow += 1;
  } else {
    photos.forEach((photo) => {
      const row = worksheet.getRow(currentRow);
      row.getCell(1).value = asText(photo.pg);
      row.getCell(2).value = asText(photo.obs);
      row.height = estimateStructureRowHeight(photo.obs);
      const photoUrl = asText(photo.firebasePath);
      row.getCell(3).value = /^https?:\/\//i.test(photoUrl)
        ? { text: "Abrir foto", hyperlink: photoUrl }
        : photoUrl;
      if (/^https?:\/\//i.test(photoUrl)) {
        row.getCell(3).font = { color: { argb: "FF0563C1" }, underline: true };
      }
      currentRow += 1;
    });
  }
  styleTableRows(worksheet, structureStartRow, currentRow - 1, 3);
  currentRow += 1;

  addSectionTitle(worksheet, currentRow, "MATERIAIS", "G");
  currentRow += 1;
  const materialsHeaderRow = worksheet.getRow(currentRow);
  materialsHeaderRow.values = ["DESCRIÇÃO", "CÓDIGO (MATERIAL)", "QUANTIDADE", "AÇÃO"];
  styleHeaderRow(materialsHeaderRow);
  const materialStartRow = currentRow + 1;
  currentRow += 1;

  const materials = Array.isArray(invoice.materials)
    ? invoice.materials.filter((material) => material && typeof material === "object")
    : [];

  if (materials.length === 0) {
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = "Nenhum material registrado para este boletim.";
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "center" };
    currentRow += 1;
  } else {
    materials.forEach((material) => {
      const row = worksheet.getRow(currentRow);
      row.values = [
        asText(material.descriptions),
        asText(material.material),
        asText(material.quantity),
        asText(material.action),
      ];
      currentRow += 1;
    });
  }
  styleTableRows(worksheet, materialStartRow, currentRow - 1, 4);

  worksheet.views = [{ state: "frozen", ySplit: 2, showGridLines: false }];
  worksheet.autoFilter = {
    from: { row: 17, column: 1 },
    to: { row: Math.max(17, 17 + services.length), column: 7 },
  };

  await Promise.all(
    photos.map((photo, index) =>
      embedStructurePhoto(
        workbook,
        worksheet,
        structureStartRow + index,
        asText(photo.firebasePath),
      ),
    ),
  );

  return workbook;
};

const sanitizeFilename = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "boletim";

export const exportInvoiceToExcel = async (
  invoice: Invoice,
  currentFactor: number,
): Promise<void> => {
  const workbook = await buildInvoiceWorkbook(invoice, currentFactor);
  const data = await workbook.xlsx.writeBuffer();
  const blob = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `relatorio-${sanitizeFilename(invoice.id || invoice.key)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
